import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../config/api';
import axios from '../../config/axiosConfig';
import LoadingOverlay from '../LoadingOverlay';
import Toast from '../Toast';
import { normalizeRestaurantId } from '../../utils/restaurantUtils';

const WaiterAttendance = ({ waiterId, restaurantId: restaurantIdProp }) => {
    // Normalize restaurantId to ensure it's always a string
    const restaurantId = normalizeRestaurantId(restaurantIdProp);
    const [attendance, setAttendance] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    useEffect(() => {
        if (waiterId) {
            fetchTodayAttendance();
        }
    }, [waiterId]);

    const fetchTodayAttendance = async () => {
        try {
            setLoading(true);
            const response = await axios.get(API_ENDPOINTS.ATTENDANCE_GET, {
                params: { 
                    staffId: waiterId, 
                    date: new Date().toISOString().split('T')[0] 
                }
            });
            if (response.data && response.data.length > 0) {
                setAttendance(response.data[0]);
            }
        } catch (error) {
            console.error('Failed to fetch attendance:', error);
        } finally {
            setLoading(false);
        }
    };

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    };

    const getCurrentShift = () => {
        const hour = new Date().getHours();
        if (hour >= 9 && hour < 15) return 'MORNING';
        if (hour >= 15 && hour < 22) return 'EVENING';
        return 'NIGHT';
    };

    const handleCheckIn = async (shift) => {
        setActionLoading(true);
        setLoadingMessage('Checking in...');
        try {
            const response = await axios.post(API_ENDPOINTS.ATTENDANCE_CHECKIN, {
                staffId: waiterId,
                shift,
                restaurantId
            });
            setAttendance(response.data);
            showToast('Checked in successfully');
        } catch (error) {
            showToast(error.response?.data?.message || 'Check-in failed', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleCheckOut = async () => {
        if (!attendance || attendance.checkOutTime) return;
        
        setActionLoading(true);
        setLoadingMessage('Checking out...');
        try {
            const response = await axios.post(API_ENDPOINTS.ATTENDANCE_CHECKOUT, {
                staffId: waiterId,
                shift: attendance.shift
            });
            setAttendance(response.data);
            showToast('Checked out successfully');
        } catch (error) {
            showToast(error.response?.data?.message || 'Check-out failed', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
        );
    }

    const currentShift = getCurrentShift();
    const isCheckedIn = attendance && !attendance.checkOutTime;
    const status = isCheckedIn ? 'PRESENT' : attendance?.status || 'ABSENT';

    return (
        <div className="relative">
            {actionLoading && <LoadingOverlay message={loadingMessage} />}

            <div className="max-w-2xl mx-auto">
                <h2 className="text-2xl font-bold mb-6">Today's Attendance</h2>

                {/* Attendance Card */}
                <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
                    <div className="text-center mb-6">
                        <div className={`inline-block px-6 py-3 rounded-full text-lg font-semibold ${
                            status === 'PRESENT' ? 'bg-green-100 text-green-800' :
                            status === 'LATE' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                        }`}>
                            {status === 'PRESENT' ? '🟢 Present' :
                             status === 'LATE' ? '🟡 Late' :
                             '🔴 Absent'}
                        </div>
                    </div>

                    <div className="space-y-4">
                        {attendance?.checkInTime && (
                            <div className="flex justify-between items-center py-3 border-b">
                                <span className="text-gray-600">Check In:</span>
                                <span className="font-semibold">
                                    {new Date(attendance.checkInTime).toLocaleString()}
                                </span>
                            </div>
                        )}
                        {attendance?.checkOutTime && (
                            <div className="flex justify-between items-center py-3 border-b">
                                <span className="text-gray-600">Check Out:</span>
                                <span className="font-semibold">
                                    {new Date(attendance.checkOutTime).toLocaleString()}
                                </span>
                            </div>
                        )}
                        {attendance?.totalHours > 0 && (
                            <div className="flex justify-between items-center py-3 border-b">
                                <span className="text-gray-600">Total Hours:</span>
                                <span className="font-semibold">{attendance.totalHours} hrs</span>
                            </div>
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div className="mt-6 flex gap-4">
                        {!isCheckedIn ? (
                            <button
                                onClick={() => handleCheckIn(currentShift)}
                                className="flex-1 bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
                            >
                                ✓ Check In
                            </button>
                        ) : (
                            <button
                                onClick={handleCheckOut}
                                className="flex-1 bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors"
                            >
                                ✗ Check Out
                            </button>
                        )}
                    </div>
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                        <strong>Note:</strong> Attendance is automatically recorded. You cannot edit your attendance records.
                    </p>
                </div>
            </div>

            {toast.show && <Toast message={toast.message} type={toast.type} />}
        </div>
    );
};

export default WaiterAttendance;

